import {
  Button,
  Heading,
  PillsContainer,
  ProductSuggestions,
  LoadingSpinner,
} from '@rabbit/elements/shared-components';
import SearchGallery from '../../components/molecules/SearchGallery/SearchGallery';
import SearchSlider from '../../components/molecules/SearchSlider/SearchSlider';
import SearchFilter from '../../components/molecules/SearchFilter/SearchFilter';
import ROUTE_NAME from '../../utils/url-constants';
import { ChangeEvent, useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DTVendable, FBD_Vendable } from '@rabbit/data/types';
import { useTranslation } from 'react-i18next';
import { AppContext } from '@rabbit/app-context';
import { QueueListIcon } from '@heroicons/react/24/solid';
import { Squares2X2Icon } from '@heroicons/react/24/solid';

interface GroupedResults {
  [type: string]: DTVendable[];
}

interface VendableSearchContainerProps {
  onChangeStep?: (step_in: number, data: any) => void;
}

export function VendableSearchContainerFirestore({
  onChangeStep,
}: VendableSearchContainerProps) {
  const [searchInput, setSearchInput] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string[]>([]);
  const [results, setResults] = useState<GroupedResults>();
  const [filteredResults, setFilteredResults] = useState<GroupedResults>();
  const [noResults, setNoResults] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [viewType, setViewType] = useState<'grid' | 'list'>(() => {
    // Get saved preference from localStorage or default to 'grid'
    const savedViewType = localStorage.getItem('productViewType');
    return (savedViewType === 'list' || savedViewType === 'grid') ? savedViewType : 'grid';
  });
  const navigate = useNavigate();

  const { config } = useContext(AppContext);
  const { t } = useTranslation();

  const QUERY_PARAM = 'searchParams';
  const [searchParams, setSearchParams] = useSearchParams();

  const findResults = async (searchQuery: string) => {
    setIsLoading(true);
    try {
      let query = FBD_Vendable.query();

      query = query
        .where('mfr', '==', `M:${t('tenantLink')}`)
        .where('published', '==', true);

      const vendables = await query.getContents();
      const groupedData = groupResultsByCategory(vendables, 'category');

      if (Object.values(groupedData).length) {
        setResults(groupedData);
        setFilteredResults(groupedData);
        setNoResults(false);

        // Ensure viewType is maintained after results update
        const currentViewType = localStorage.getItem('productViewType') || viewType;
        if (currentViewType !== viewType) {
          setViewType(currentViewType as 'grid' | 'list');
        }
      } else {
        setResults({});
        setFilteredResults({});
        setNoResults(true);
      }

      return groupedData;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Check if we have search params on initial load
    const paramValues = searchParams.get(QUERY_PARAM) || '';
    const paramsArray = paramValues ? paramValues.split(',') : [];

    if (paramsArray.length > 0 && paramsArray[0]?.length) {
      // If we have search params, set them and let the other useEffect handle the filtering
      setSearchValue(paramsArray);
    } else {
      // Otherwise, just load all results
      findResults('');
    }
  }, []); // Empty dependency array to ensure it only runs once on mount

  // Handle filter changes from the SearchFilter component
  const handleFilterChange = (allData: DTVendable[]) => {
    if (!results) return;

    // Create a new filtered results object with the same structure as results
    const newFilteredResults: GroupedResults = {};

    // For each category in results
    Object.keys(results).forEach(category => {
      // Filter the data for this category
      const categoryData = results[category];
      const filteredCategoryData = allData.filter(item =>
        categoryData.some(catItem => catItem.docid === item.docid)
      );

      // Only add the category if it has items after filtering
      if (filteredCategoryData.length > 0) {
        newFilteredResults[category] = filteredCategoryData;
      }
    });

    setFilteredResults(newFilteredResults);
  };

  const updateQueryString = (arr: string[]) => {
    if (arr?.length) {
      setSearchParams({ [QUERY_PARAM]: arr.join(',').toLowerCase() });
    } else {
      setSearchParams({});
    }
  };

  const addValue = (value: string) => {
    if (!searchValue.includes(value)) {
      // If there are no results, just use the new value
      // Otherwise, add the new value to the existing array
      const newArray = noResults ? [value] : [...searchValue, value];
      updateQueryString(newArray);
    }
  };

  const groupResultsByCategory = (
    vendables: DTVendable[],
    property: string
  ) => {
    const groupedData = vendables.reduce((groupedData, currentItem: any) => {
      const [propertyValues] = currentItem[property].slice(-1);

      if (propertyValues && !groupedData[propertyValues]) {
        groupedData[propertyValues] = [];
      }

      if (propertyValues && groupedData[propertyValues]) {
        groupedData[propertyValues].push(currentItem);
      }

      return groupedData;
    }, {} as Record<string, DTVendable[]>);

    return groupedData;
  };

  const handleSubmitSearch = (event?: { key: string }) => {
    if (!searchInput) return;
    if (event && event.key !== 'Enter') return;

    addValue(searchInput);
    setSearchInput('');
  };

  const handleQuickSearch = (value: string) => {
    addValue(value);
  };

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    event.preventDefault();
    setSearchInput(event.target.value);
  };

  const handleShowMore = async (value: string) => {
    if (!searchValue.includes(value)) {
      addValue(value);
    }

    // Filter results to show only the selected category
    const categoryResults = Object.keys(results || {}).reduce((acc, key) => {
      // If the category name matches the value (which is the category name)
      if (key.replace(/-/g, ' ').toLowerCase() === value.toLowerCase()) {
        acc[key] = (results || {})[key];
      }
      return acc;
    }, {} as GroupedResults);

    // Force list view when a category is selected
    setViewType('list');
    localStorage.setItem('productViewType', 'list');

    setResults(categoryResults);
    setFilteredResults(categoryResults);
  };

  const handleDeleteSearchItem = (index: number, value: string) => {
    const newSearchValue = [...searchValue];
    newSearchValue.splice(index, 1);
    setSearchValue(newSearchValue);
    updateQueryString(newSearchValue); // updateQueryString already handles empty arrays
  };

  useEffect(() => {
    if (searchValue.length) {
      findResults(searchValue.join(','))
        .then((data) => {
          console.log('success:', data);
          setFilteredResults(data);

          // Force list view when searchParams are present
          setViewType('list');
          localStorage.setItem('productViewType', 'list');
        })
        .catch((err) => console.log(err));
    } else {
      setResults([] as unknown as GroupedResults);
      setFilteredResults([] as unknown as GroupedResults);
    }
  }, [searchValue]);

  useEffect(() => {
    const paramValues = searchParams.get(QUERY_PARAM) || '';
    const paramsArray = paramValues ? paramValues.split(',') : [];

    // Check if the arrays are different
    const isDifferent =
      paramsArray.length !== searchValue.length ||
      paramsArray.some((value, index) => value !== searchValue[index]);

    if (isDifferent) {
      if (paramsArray.length > 0 && paramsArray[0]?.length) {
        setSearchValue(paramsArray);

        // Force list view when searchParams are present
        setViewType('list');
        localStorage.setItem('productViewType', 'list');

        // If we have search params, filter to show only that category
        if (results) {
          const categoryResults = Object.keys(results).reduce((acc, key) => {
            // Check if the category name matches any of the search parameters
            if (paramsArray.some(param =>
              key.replace(/-/g, ' ').toLowerCase() === param.toLowerCase()
            )) {
              acc[key] = results[key];
            }
            return acc;
          }, {} as GroupedResults);

          setFilteredResults(categoryResults);
        }
      } else {
        setSearchValue([]);
      }
    }
  }, [searchParams, QUERY_PARAM, results]); // Added results to dependencies

  // Function to get all products from all categories
  const getAllProducts = () => {
    if (!results) return [];

    const allProducts: DTVendable[] = [];
    Object.values(results).forEach(categoryProducts => {
      allProducts.push(...categoryProducts);
    });

    return allProducts;
  };

  return (
    <>
      {searchValue.length > 0 && (
        <div className="mt-4">
          <PillsContainer
            values={searchValue}
            onClick={handleDeleteSearchItem}
          />
        </div>
      )}
      <div>
        {isLoading ? (
          <LoadingSpinner size="md" />
        ) : (
          <>
            {noResults && searchValue.length ? (
              <div className="mt-[25px] mb-4">
                <p className="font-nunito font-bold">No products were found.</p>
              </div>
            ) : null}
            {(results && Object.keys(results).length < 1) ||
            !results ||
            noResults ? (
              <>
                <div className="mt-[25px]">
                  <ProductSuggestions
                    submitSearchCallback={handleQuickSearch}
                  />
                </div>
                <div className="mt-8 flex flex-col gap-4">
                  <Button
                    kind="primary"
                    children="Scan product barcode or QR code"
                    onClick={() =>
                      onChangeStep
                        ? onChangeStep(99, null)
                        : navigate(ROUTE_NAME.PRODUCTS_SCAN)
                    }
                  />
                  {!onChangeStep &&
                    config.HOLDINGS.NEW_REGISTRATION_FLOW
                      .ADD_MANUAL_PRODUCT && (
                      <>
                        <Heading kind="p" className="mx-auto">
                          or
                        </Heading>
                        <Button
                          kind="outline"
                          children="Add manually"
                          onClick={() => navigate(ROUTE_NAME.PRODUCTS_ADD)}
                        />
                      </>
                    )}
                </div>
              </>
            ) : (
              <>
                {/* Common search and filter component */}
                {results && Object.keys(results).length > 0 && (
                  <div className="mt-4 mb-6">
                    <SearchFilter
                      data={getAllProducts()}
                      onFilterChange={handleFilterChange}
                      className="w-full"
                      viewType={viewType}
                      onViewTypeChange={(type) => {
                        setViewType(type);
                        localStorage.setItem('productViewType', type);
                      }}
                    />
                  </div>
                )}

                {filteredResults
                  ? Object.keys(filteredResults).map((key, index) => (
                      <div key={index}>
                        {Object.keys(filteredResults).length > 1 ? (
                          <div className="mt-8">
                            <SearchSlider
                              title={key.replace(/-/g, ' ')}
                              actionButtonText="Show All"
                              data={filteredResults[key]}
                              onClick={handleShowMore}
                              viewType={viewType}
                              hideShowMore={searchValue.length > 0}
                            />
                          </div>
                        ) : (
                          <div className="py-2 lg:pt-8">
                            <SearchGallery
                              onChangeStep={onChangeStep}
                              data={filteredResults[key]}
                              viewType={viewType}
                            />
                          </div>
                        )}
                      </div>
                    ))
                  : null}
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}

export default VendableSearchContainerFirestore;
